import { z } from "zod";
/**
 * Place energy schemas — the shared contract for the Phase 4 surface
 * (apps/api/src/routes/places/placeUtilities.ts): utility accounts + readings on the ESPM
 * meter model. Writes are Pattern B (permissive member RLS + a RESTRICTIVE
 * verify_backend_token() gate). The DERIVED scope columns (operational_control,
 * ghg_scope, scope_3_category) and quantity_internal are NEVER client inputs —
 * scope is trigger-set; quantity_internal is computed server-side via the units
 * shim. See docs/places/phase-4.md and docs/BACKEND_API.md.
 */
const dateString = z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, "expected a YYYY-MM-DD date");
const nullableText = (max) => z.string().max(max).nullable().optional();
const currency = z.string().length(3).nullable().optional();
// ---------------------------------------------------------------------------
// Enums (mirror the DB CHECKs)
// ---------------------------------------------------------------------------
export const utilityDomainSchema = z.enum(["energy", "water", "waste"]);
export const wasteMethodSchema = z.enum([
    "disposed",
    "recycled",
    "composted",
    "donated_reused",
]);
export const disposedDestinationSchema = z.enum([
    "landfill",
    "incineration",
    "waste_to_energy",
    "other_unknown",
]);
export const paymentResponsibilitySchema = z.enum([
    "we_pay",
    "landlord_pays",
    "shared",
    "not_applicable",
]);
export const partySchema = z.enum(["landlord", "tenant"]);
export const coverageSchema = z.enum([
    "whole_building",
    "base_building",
    "tenant_space",
    "common_areas",
    "shared_services",
]);
export const greenPowerSourceSchema = z.enum(["green_tariff", "recs", "ppa", "unknown"]);
export const estimationMethodSchema = z.enum([
    "floor_area_benchmark",
    "usage_hours",
    "landlord_data",
]);
export const managementModeSchema = z.enum(["manual", "managed"]);
export const entryMethodSchema = z.enum(["manual", "csv", "estimated", "import"]);
export const wasteSubTypeSchema = z.enum(["construction", "general", "business"]);
/**
 * Why an account is excluded from every consumption aggregate AND from carbon
 * projection (NULL = counted). A reason, not a boolean — the why must not go
 * stale. Sub-meters (`parent_account_id`) are a DIFFERENT mechanism: their
 * energy counts once, via the parent. Vocabulary extends by migration (CHECK
 * on place_utility_accounts). Canonical contract: docs/PLACES.md.
 */
export const rollupExclusionReasonSchema = z.enum([
    "espm_metrics",
    "reference_only",
    "duplicate",
    "not_our_consumption",
]);
// ---------------------------------------------------------------------------
// Utility accounts
// ---------------------------------------------------------------------------
const utilityAccountCore = {
    name: nullableText(200),
    meter_type_id: z.string().uuid().nullable().optional(),
    // Waste is now classified per reading row; the account carries only the stream.
    waste_sub_type: wasteSubTypeSchema.nullable().optional(),
    /** The meter this one sits INSIDE — its readings are already in the parent's. */
    parent_account_id: z.string().uuid().nullable().optional(),
    /** NULL = counted; a value = excluded-with-reason from every aggregate and
     * from carbon projection. Reason wins over parent when both are set. */
    rollup_exclusion_reason: rollupExclusionReasonSchema.nullable().optional(),
    waste_density_kg_per_yd3: z.number().finite().positive().nullable().optional(),
    account_number: nullableText(200),
    provider: nullableText(200),
    source_unit: nullableText(40),
    currency,
    payment_responsibility: paymentResponsibilitySchema.optional(),
    procured_by: partySchema.nullable().optional(),
    controlled_by: partySchema.nullable().optional(),
    coverage: coverageSchema.optional(),
    green_power: z.boolean().optional(),
    green_power_pct: z.number().finite().min(0).max(100).nullable().optional(),
    green_power_source: greenPowerSourceSchema.nullable().optional(),
    estimation_method: estimationMethodSchema.nullable().optional(),
    management_mode: managementModeSchema.optional(),
    // Emissions overrides (places_emissions): the meter's explicit emission
    // region (e.g. an eGRID subregion) and an explicit factor-table pin that
    // replaces the meter-type binding (single NULL-basis result; the engine
    // still picks the vintage; classification is untouched).
    emission_region_code: nullableText(20),
    emission_factor_table_id: z.string().uuid().nullable().optional(),
    emission_factor_dimension_filter: z
        .record(z.string(), z.string())
        .nullable()
        .optional(),
    external_source: nullableText(100),
    external_id: nullableText(200),
    metadata: z.record(z.string(), z.unknown()).optional(),
};
export const createUtilityAccountSchema = z.object({
    domain: utilityDomainSchema,
    ...utilityAccountCore,
});
export const updateUtilityAccountSchema = z.object(utilityAccountCore).partial();
// ---------------------------------------------------------------------------
// Utility readings — quantity_internal is computed server-side (NOT an input)
// ---------------------------------------------------------------------------
const utilityReadingCore = {
    period_start: dateString,
    period_end: dateString,
    quantity: z.number().finite().nullable().optional(),
    unit: nullableText(40),
    cost: z.number().finite().nullable().optional(),
    // cost_currency is NOT a client input — it's the account's currency, set server-side.
    off_peak_quantity: z.number().finite().min(0).nullable().optional(),
    renewable_quantity: z.number().finite().min(0).nullable().optional(),
    is_estimated: z.boolean().optional(),
    demand_kw: z.number().finite().min(0).nullable().optional(),
    entry_method: entryMethodSchema.optional(),
    notes: nullableText(4000),
    // Per-row waste classification (waste accounts only): material + end state.
    waste_material_id: z.string().uuid().nullable().optional(),
    waste_method: wasteMethodSchema.nullable().optional(),
    disposed_destination: disposedDestinationSchema.nullable().optional(),
    external_source: nullableText(100),
    external_id: nullableText(200),
    ap_invoice_id: z.string().uuid().nullable().optional(),
    metadata: z.record(z.string(), z.unknown()).optional(),
};
export const createUtilityReadingSchema = z
    .object(utilityReadingCore)
    .refine((v) => v.period_end >= v.period_start, {
    message: "period_end must be on or after period_start",
    path: ["period_end"],
});
/**
 * PATCH input. `entry_method` is deliberately OMITTED: it is provenance (how the
 * row arrived — manual / csv / import / estimated) and must be write-once, so
 * editing a CSV-imported reading can't silently reclassify it as hand-entered.
 * Zod strips unknown keys, so a client that sends it is ignored rather than
 * rejected. The bulk/import path still sets it (it owns the import contract).
 */
export const updateUtilityReadingSchema = z
    .object(utilityReadingCore)
    .omit({ entry_method: true })
    .partial();
/**
 * Bulk upload (CSV / ESPM import) — an array of readings for one account.
 * Each row carries the SAME period-order refine as a single create, so the bulk
 * path fails with a readable message instead of letting the DB CHECK
 * (`place_utility_readings_period_order`) be the first line of defence.
 */
export const bulkUtilityReadingsSchema = z.object({
    readings: z
        .array(z.object(utilityReadingCore).refine((v) => v.period_end >= v.period_start, {
        message: "period_end must be on or after period_start",
        path: ["period_end"],
    }))
        .min(1)
        .max(2000),
});
/** One id-keyed patch inside a batch save (entry_method inherited from update). */
export const utilityReadingPatchSchema = updateUtilityReadingSchema.extend({
    id: z.string().uuid(),
});
/**
 * The readings grid's Save, as ONE atomic unit. The server applies these in a
 * single transaction, in the order DELETE → UPDATE → CREATE (updates must
 * precede creates so a row whose period moves vacates the slot first).
 */
export const saveReadingsBatchSchema = z.object({
    deletes: z.array(z.string().uuid()).max(2000).default([]),
    updates: z.array(utilityReadingPatchSchema).max(2000).default([]),
    // Same period-order refine as the single-create and bulk paths, so the batch
    // reports "period_end must be on or after period_start" rather than leaking a
    // raw `place_utility_readings_period_order` constraint violation to the user.
    creates: z
        .array(z.object(utilityReadingCore).refine((v) => v.period_end >= v.period_start, {
        message: "period_end must be on or after period_start",
        path: ["period_end"],
    }))
        .max(2000)
        .default([]),
});
// ---------------------------------------------------------------------------
// Query params
// ---------------------------------------------------------------------------
export const energyTrendQuerySchema = z.object({
    from: dateString.optional(),
    to: dateString.optional(),
});
export const utilityBreakdownQuerySchema = z.object({
    domain: z.enum(["energy", "water"]).optional(),
    from: dateString.optional(),
    to: dateString.optional(),
    bucket: z.enum(["month", "year"]).optional(),
});
// ---------------------------------------------------------------------------
// Waste analytics (place_waste_monthly rollup reads)
// ---------------------------------------------------------------------------
export const wasteBreakdownQuerySchema = z.object({
    from: dateString.optional(),
    to: dateString.optional(),
    bucket: z.enum(["month", "year"]).optional(),
});
export const wasteMaterialTotalsQuerySchema = z.object({
    from: dateString.optional(),
    to: dateString.optional(),
});
/* ---------------------------------------------------------------------------
 * Utility emissions (docs/places/phase-7-emissions.md)
 * ------------------------------------------------------------------------- */
/**
 * POST /orgs/:orgId/emissions/recalculate — one CHUNK of the stale set per
 * call (bounded inside Vercel's maxDuration); the client loops on nextCursor.
 * dryRun runs the engine and reports without writing — the preview for a
 * basis toggle or factor-seed upgrade.
 */
export const recalculateEmissionsSchema = z
    .object({
    placeId: z.string().uuid().optional(),
    accountId: z.string().uuid().optional(),
    from: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional(),
    to: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional(),
    cursor: z
        .object({
        account: z.string().uuid(),
        month: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
    })
        .optional(),
    dryRun: z.boolean().optional(),
})
    .strict();
/** PATCH …/emissions/settings — the org's Scope-2 reporting knobs (admin). */
export const emissionsSettingsSchema = z
    .object({
    reporting_basis: z.enum(["location_based", "market_based"]).optional(),
    default_region_code: z.string().min(1).max(20).nullable().optional(),
})
    .strict();
/** GET …/places/:placeId/emissions/breakdown query. */
export const emissionsBreakdownQuerySchema = z
    .object({
    basis: z.enum(["location_based", "market_based"]).optional(),
    from: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional(),
    to: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional(),
    bucket: z.enum(["month", "year"]).optional(),
})
    .strict();
/** GET …/emissions/bindings query — asOf drives the vintage the health check tests. */
export const emissionsBindingsQuerySchema = z
    .object({
    asOf: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional(),
})
    .strict();
//# sourceMappingURL=placeUtilities.js.map